import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { buildWhatsAppSummary } from '../lib/whatsappSummary';
import { WHATSAPP_NUMBER } from '../lib/api';
import { MessageCircle, Copy, Check, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const STORAGE_KEY = 'app_selfcheck';

export default function WhatsAppSummary() {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const data = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  }, []);

  const summary = useMemo(() => buildWhatsAppSummary(data, lang), [data, lang]);
  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(summary)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      toast.success(t('whatsapp_copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed');
    }
  };

  const hasAnyConcern = data && (data.primary_concern || data.main_concern);
  if (!hasAnyConcern) {
    return (
      <div className="container-app py-12 text-center">
        <p className="text-ink-secondary mb-4">
          {lang === 'zh' ? '请先完成自我检测' : 'Please complete the self-check first'}
        </p>
        <button onClick={() => navigate('/self-check')} className="btn-primary">
          {t('nav_self_check')}
        </button>
      </div>
    );
  }

  return (
    <div className="container-app py-8 sm:py-12 max-w-2xl animate-fade-in-up" data-testid="whatsapp-summary-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-ink-secondary hover:text-terracotta mb-6">
        <ChevronLeft size={16} /> {t('back')}
      </button>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink mb-2">
        {t('whatsapp_title')}
      </h1>
      <p className="text-ink-secondary mb-8">{t('whatsapp_subtitle')}</p>

      <div className="card-base p-5 sm:p-7 mb-6">
        <pre
          data-testid="summary-text"
          className="whitespace-pre-wrap font-sans text-sm sm:text-base text-ink-secondary leading-relaxed"
        >
          {summary}
        </pre>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <a
          href={waLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-open-btn"
          className="btn-primary"
        >
          <MessageCircle size={18} className="mr-2" />
          {t('whatsapp_open')}
        </a>
        <button onClick={copy} data-testid="copy-summary-btn" className="btn-outline">
          {copied ? <Check size={18} className="mr-2" /> : <Copy size={18} className="mr-2" />}
          {copied ? t('whatsapp_copied') : t('whatsapp_copy')}
        </button>
      </div>
    </div>
  );
}
